// Site-wide offers (2026-09-30 CRO pass, docs/cro-playbook.md): the top bar countdown (video 12)
// and the mystery-discount pop-up (video 10), whose code is applied to the cart so the saving
// shows at once (videos 5 and 7). Markup: snippets/snag-topbar.liquid, snippets/snag-mystery.liquid.

const store = {
  get(key) { try { return window.localStorage.getItem(key); } catch { return null; } },
  set(key, value) { try { window.localStorage.setItem(key, value); } catch { /* private mode */ } },
};
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const CLAIMED = 'snag:mystery';
const DISMISSED = 'snag:mystery-dismissed';

function addBusinessDays(date, n) {
  const d = new Date(date.getTime());
  while (n > 0) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) n -= 1;
  }
  return d;
}

/* ── top bar ──────────────────────────────────────────────────────── */
class SnagTopbar extends HTMLElement {
  connectedCallback() {
    this.items = Object.fromEntries([...this.querySelectorAll('[data-top]')].map((el) => [el.dataset.top, el]));
    this.clock = this.querySelector('[data-top-left]');
    const [y, m, d] = (this.dataset.deadline || '').split('-').map(Number);
    if (y) {
      // Last day an order still arrives by the target date at the slow end of delivery; the
      // countdown runs to the end of that day, local time.
      const target = new Date(y, m - 1, d);
      const last = new Date(target.getTime());
      const days = Number(this.dataset.days) || 16;
      while (addBusinessDays(last, days) > target) last.setDate(last.getDate() - 1);
      last.setHours(23, 59, 59, 999);
      this.end = last;
    }
    this.render();
    document.addEventListener('snag:mystery-claimed', () => this.render());
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.observer?.disconnect();
  }

  show(mode) {
    for (const [key, el] of Object.entries(this.items)) el.hidden = key !== mode;
  }

  // Minute resolution, and only while the bar is on screen and the tab is visible. A per-second
  // tick changed the page every second, and with the theme's :has() selectors each change forced a
  // whole-page style recalculation (about 3 s of main-thread work per 10 s on a mid phone), which
  // session-recording scripts then had to process too.
  tick() {
    window.clearTimeout(this.timer);
    const left = this.end.getTime() - Date.now();
    if (left <= 0) { this.render(); return; }
    const mins = Math.ceil(left / 60000);
    const dd = Math.floor(mins / 1440);
    const hh = Math.floor((mins % 1440) / 60);
    const mm = mins % 60;
    const text = dd > 0 ? `${dd}d ${hh}h ${String(mm).padStart(2, '0')}m` : `${hh}h ${String(mm).padStart(2, '0')}m`;
    if (this.clock.textContent !== text) this.clock.textContent = text;
    if (this.onScreen !== false && document.visibilityState === 'visible') {
      this.timer = window.setTimeout(() => this.tick(), (left % 60000) + 50);
    }
  }

  render() {
    window.clearTimeout(this.timer);
    if (this.end && Date.now() < this.end.getTime()) {
      this.show('deadline');
      this.tick();
      if (!this.observer && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.onScreen = entries[0].isIntersecting;
          if (this.onScreen) this.tick(); else window.clearTimeout(this.timer);
        });
        this.observer.observe(this);
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.tick(); });
      }
      return;
    }
    const offer = this.items.offer && this.dataset.mystery === 'true' && store.get(CLAIMED) !== 'claimed';
    this.show(offer ? 'offer' : 'plain');
  }
}

/* ── applying the code to the cart ───────────────────────────────── */
async function applyCode(code) {
  // The shareable-link endpoint keeps the code for checkout even if the cart is still empty.
  await fetch(`${root()}discount/${encodeURIComponent(code)}`, { redirect: 'manual', credentials: 'same-origin' }).catch(() => {});
  const cart = await fetch(`${root()}cart.js`).then((r) => r.json()).catch(() => null);
  if (!cart || !cart.item_count) return;
  const codes = (cart.discount_codes || []).map((d) => d.code.toUpperCase());
  if (codes.includes(code.toUpperCase())) return;
  if (codes.length) return; // the shopper entered their own code: leave it
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const res = await fetch(`${root()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: code, sections: sectionIds }),
  }).then((r) => r.json()).catch(() => null);
  if (!res || !res.sections) return;
  try {
    const { morphSection } = await import('@theme/section-renderer');
    for (const id of sectionIds) if (res.sections[id]) morphSection(id, res.sections[id]);
  } catch { /* the next page load shows it */ }
}

/* ── mystery pop-up ───────────────────────────────────────────────── */
function initMystery() {
  const dialog = document.querySelector('[data-snag-mystery]');
  if (!dialog) return;
  const code = dialog.dataset.code;
  const form = dialog.querySelector('form');
  const email = dialog.querySelector('input[type="email"]');
  const err = dialog.querySelector('[data-mys-err]');
  const claimed = () => store.get(CLAIMED) === 'claimed';
  // Not on pages with a job of their own; not on article pages, which ads send cold traffic to.
  const quietPage = /^\/(cart|account|pages\/(review|store-credit|package-protection|contact))/.test(window.location.pathname)
    || !!document.querySelector('[data-snag-article]');

  const open = () => {
    if (dialog.open) return;
    const step = claimed() ? 'reveal' : 'form';
    dialog.querySelectorAll('[data-mys-step]').forEach((el) => { el.hidden = el.dataset.mysStep !== step; });
    dialog.showModal();
    if (step === 'form') window.setTimeout(() => email?.focus({ preventScroll: true }), 50);
  };
  const close = () => {
    if (!dialog.open) return;
    dialog.close();
    if (!claimed()) store.set(DISMISSED, String(Date.now()));
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-snag-mystery-open]')) { event.preventDefault(); open(); }
  });
  dialog.querySelectorAll('[data-mys-close]').forEach((b) => b.addEventListener('click', close));
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('cancel', () => { if (!claimed()) store.set(DISMISSED, String(Date.now())); });

  // The reveal stays on screen until the shopper closes it.
  const reveal = () => {
    const art = dialog.querySelector('[data-mys-art]');
    if (art) {
      art.textContent = dialog.querySelector('.snag-mys__big')?.textContent.replace(/\D/g, '') || '10';
      art.parentElement.classList.add('is-revealed');
    }
    dialog.querySelectorAll('[data-mys-step]').forEach((el) => { el.hidden = el.dataset.mysStep !== 'reveal'; });
    const big = dialog.querySelector('.snag-mys__big');
    if (big) { big.setAttribute('tabindex', '-1'); big.focus({ preventScroll: true }); }
  };

  // Once claimed, keep the code on the cart as products are added (also right after claiming).
  let keeping = false;
  const keepCodeOnCart = () => {
    if (keeping) return;
    keeping = true;
    import('@shopify/events').then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        event.promise?.then(() => window.setTimeout(() => applyCode(code), 300)).catch(() => {});
      });
    }).catch(() => {});
  };

  const claim = () => {
    store.set(CLAIMED, 'claimed');
    reveal();
    applyCode(code);
    keepCodeOnCart();
    document.dispatchEvent(new CustomEvent('snag:mystery-claimed'));
    syncPdp();
  };

  // Shopify's spam check (hCaptcha) attaches its own submit handling to this form once a field is
  // focused, then submits it the classic way, which used to reload the page and wipe the reveal.
  // The form now targets a hidden frame, so that submission never leaves the page. This handler
  // runs first (capture): it checks the email, shows the reveal at once, and hands the sending to
  // form.submit(), which is Shopify's captcha-aware version once the spam check is attached.
  let sent = false;
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    const value = email.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) { err.hidden = false; email.focus(); return; }
    err.hidden = true;
    if (sent) return;
    sent = true;
    claim();
    const send = () => { try { form.submit(); } catch { HTMLFormElement.prototype.submit.call(form); } };
    if (form.hasAttribute('data-hcaptcha-bound')) { send(); return; }
    // The spam check may still be loading: give it up to 4 s, then send either way.
    let waited = 0;
    const timer = window.setInterval(() => {
      waited += 100;
      if (form.hasAttribute('data-hcaptcha-bound') || waited >= 4000) { window.clearInterval(timer); send(); }
    }, 100);
  }, true);

  dialog.querySelector('[data-mys-copy]')?.addEventListener('click', async (event) => {
    try { await navigator.clipboard.writeText(code); event.target.textContent = 'Copied'; } catch { event.target.textContent = code; }
  });

  if (claimed()) keepCodeOnCart();

  // A sign-up that reloaded the page anyway (an old tab, or no JavaScript) comes back with
  // ?customer_posted=true#SnagMysteryForm: show the reveal and tidy the address bar.
  const params = new URLSearchParams(window.location.search);
  if (params.get('customer_posted') === 'true' && window.location.hash === `#${form?.id}`) {
    params.delete('customer_posted');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : ''));
    claim();
    open();
    return;
  }

  // Automatic opening: once per week at most, never after claiming, never on quiet pages, and
  // only after the cookie banner is out of the way.
  if (claimed() || quietPage) return;
  const last = Number(store.get(DISMISSED) || 0);
  if (Date.now() - last < 7 * 86400000) return;
  let armed = true;
  const trigger = () => { if (!armed) return; armed = false; open(); };
  const arm = () => {
    window.setTimeout(trigger, (Number(dialog.dataset.delay) || 12) * 1000);
    window.addEventListener('scroll', () => {
      const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (depth > 0.55) trigger();
    }, { passive: true });
    document.addEventListener('mouseout', (event) => { if (!event.relatedTarget && event.clientY <= 0) trigger(); });
  };
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  if (privacy && typeof privacy.shouldShowBanner === 'function' && privacy.shouldShowBanner()) {
    document.addEventListener('visitorConsentCollected', arm, { once: true });
  } else {
    arm();
  }
}

/* ── product page line: "unlock" before, "applied" after ────────── */
function syncPdp() {
  const on = store.get(CLAIMED) === 'claimed';
  document.querySelectorAll('[data-mys-pdp]').forEach((el) => { el.hidden = on; });
  document.querySelectorAll('[data-mys-pdp-on]').forEach((el) => { el.hidden = !on; });
}

/* ── sticky add-to-cart bar: step aside at the footer ───────────────── */
// The product pages' sticky bar (scripts/build-product-templates.mjs) floats over the bottom of
// the screen; once the footer is in view it would cover the policy links, so it hides there
// (the CSS lives in snag.css: html.snag-foot-in).
function watchFooter() {
  const foot = document.querySelector('.snag-foot');
  if (!foot || !('IntersectionObserver' in window)) return;
  new IntersectionObserver((entries) => {
    document.documentElement.classList.toggle('snag-foot-in', entries[0].isIntersecting);
  }).observe(foot);
}

if (!customElements.get('snag-topbar')) customElements.define('snag-topbar', SnagTopbar);
initMystery();
syncPdp();
watchFooter();
